import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const category = useMemo(
    () => categories.find((c) => c.slug === article?.category),
    [article]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 py-12 text-center">
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-3">
            文章未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            您要查看的文章可能已被移除或链接有误。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    {
      label: category?.name || article.category,
      href: `/category/${article.category}`,
    },
    { label: article.title },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.publishedAt,
        author: {
          '@type': 'Organization',
          name: '乳交完全指南',
        },
        publisher: {
          '@type': 'Organization',
          name: '乳交完全指南',
        },
        inLanguage: 'zh-CN',
        url: `https://milkguide.github.io/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <article className="px-4 md:px-6 py-6 md:py-8 max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs bg-secondary-100 text-secondary-800 px-2.5 py-1 rounded-full hover:bg-secondary-200 transition-colors duration-200 whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400">
                  {article.readTime} 分钟阅读
                </span>
                <span className="text-xs text-foreground-400">
                  {article.publishedAt}
                </span>
              </div>
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3 leading-snug">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            {/* Featured Image */}
            <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800
                [&_h2]:font-heading [&_h2]:text-lg [&_h2]:md:text-xl [&_h2]:font-semibold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-3
                [&_h3]:font-heading [&_h3]:text-base [&_h3]:md:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-2
                [&_p]:text-sm [&_p]:md:text-base [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-foreground-700
                [&_strong]:text-foreground-900 [&_strong]:font-semibold
                "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            {article.keywords && article.keywords.length > 0 && (
              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">标签：</span>
                  {article.keywords.map((kw) => (
                    <span
                      key={kw}
                      className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-10 pt-8 border-t border-background-200">
                <h4 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                  相关文章
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="bg-background-100 rounded-lg p-4 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                    >
                      <h5 className="text-sm font-medium text-foreground-800 mb-1 line-clamp-2">
                        {ra.title}
                      </h5>
                      <p className="text-xs text-foreground-500 line-clamp-2">
                        {ra.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}